import type {
	MediaContainerDesign,
	MediaContainerWithDividersDesign,
	RefArrayViewItem,
	ShapeDividersLayersEffect,
	UnitLengthPercentage,
	VariantRelationViewItem,
} from '@wix/thunderbolt-becky-types'
import type { ParsedSvg, ShapeDividerBGImage } from '@wix/thunderbolt-commons'
import { vectorImage } from '@wix/thunderbolt-commons'
import { isNumber, isNaN } from 'lodash'

const { getShapeDividerImageCss } = vectorImage
export type CompDesign = MediaContainerDesign | MediaContainerWithDividersDesign

export const getUnitLengthPercentage = (data: UnitLengthPercentage | undefined) => {
	const { value, type } = data || {}
	const unit = type === 'percentage' ? '%' : type

	return isNumber(value) && !isNaN(value) ? `${value}${unit}` : ''
}

export const getLayersEffectProp = (data: MediaContainerWithDividersDesign['dividerTop']['layersEffect']) => {
	if (!data) {
		// TODO: Validate what is the default value for size or make it optional
		return { size: 0 }
	}
	const { size, pin, x, y } = data
	return { size, pin, x: getUnitLengthPercentage(x), y: getUnitLengthPercentage(y) }
}

export const getShapeDividerArgs = ({
	compId,
	side,
	data,
}: {
	compId: string
	side: string
	data: MediaContainerWithDividersDesign['dividerTop']
}) => {
	const {
		keepAspectRatio,
		opacity,
		offsetX = {},
		shouldRepeat,
		repeatCount,
		invert: shouldInvert,
		svgId,
		layersEffect,
	} = data

	return {
		opacity,
		keepAspectRatio,
		offsetX,
		compId,
		side,
		shouldRepeat,
		repeatCount,
		shouldInvert,
		svgId,
		layersEffect: getLayersEffectProp(layersEffect),
		colorVarName: `--divider-${side}-color`,
	}
}

export const getShapeDividerImageContent = ({
	compId,
	keepAspectRatio,
	offsetX,
	side,
	shouldRepeat,
	repeatCount,
	shouldInvert,
	layersEffect,
	colorVarName,
	color,
	height,
	parsedSvg,
}: {
	compId: string
	keepAspectRatio: boolean
	offsetX: UnitLengthPercentage | {}
	side: string
	shouldRepeat: boolean
	repeatCount: number
	shouldInvert: boolean
	layersEffect: ShapeDividersLayersEffect
	colorVarName: string
	color: string
	opacity: number
	height: string
	parsedSvg: ParsedSvg
}): ShapeDividerBGImage => {
	const { info, content } = parsedSvg
	const options: vectorImage.ShapeDividerTransformationOptions = {
		keepAspectRatio,
		offsetX,
		side,
		shouldRepeat,
		repeatCount,
		shouldInvert,
		svgInfo: info,
		layersEffect,
		colorVarName,
		color,
		height,
		compId,
	}

	return getShapeDividerImageCss(content, options)
}

const isVariantRelation = (
	item: CompDesign | VariantRelationViewItem<CompDesign>
): item is VariantRelationViewItem<CompDesign> => item.type === 'VariantRelation'

const isRefArray = (item: CompDesign | RefArrayViewItem<CompDesign>): item is RefArrayViewItem<CompDesign> =>
	item.type === 'RefArray'

export const isMediaContainerWithDividers = (item: CompDesign): item is MediaContainerWithDividersDesign =>
	item.type === 'MediaContainerWithDividers'

export const getCompDesignPerVariant = (
	compDesign?: CompDesign | RefArrayViewItem<CompDesign>
): Record<string, CompDesign | undefined> => {
	if (compDesign && isRefArray(compDesign)) {
		return (compDesign.values as Array<CompDesign | VariantRelationViewItem<CompDesign>>).reduce<
			Record<string, CompDesign>
		>((acc, item) => {
			if (isVariantRelation(item)) {
				const { to } = item
				item.variants.forEach((variant) => {
					acc[variant.id] = to
				})
			} else {
				acc[''] = item
			}
			return acc
		}, {})
	} else {
		return { '': compDesign }
	}
}
